import { ENGINE } from '../config/tuning';

/**
 * Entity registry with generational handles.
 *
 * An `EntityId` packs a slot index and a generation counter into one 32-bit integer. When a slot
 * is reused its generation increments, so a stale id held by a missile seeker after its target
 * despawned fails `isAlive` instead of silently addressing whatever now occupies the slot. That
 * class of bug is nearly impossible to find by inspection later, so the handles are generational
 * from the start.
 */

const INDEX_BITS = ENGINE.ENTITY_INDEX_BITS;
const INDEX_MASK = (1 << INDEX_BITS) - 1;
const GENERATION_MASK = (1 << ENGINE.ENTITY_GENERATION_BITS) - 1;

/** Opaque packed handle: low 20 bits slot index, high 12 bits generation. */
export type EntityId = number;

/** A handle that is never alive. */
export const NULL_ENTITY: EntityId = -1;

/** Slot index of a handle. */
export function entityIndex(id: EntityId): number {
  return id & INDEX_MASK;
}

/** Generation of a handle. */
export function entityGeneration(id: EntityId): number {
  return (id >>> INDEX_BITS) & GENERATION_MASK;
}

export class Registry {
  readonly capacity: number;

  /** Generation per slot. */
  private readonly generations: Uint16Array;
  /** 1 if the slot currently holds a live entity. */
  private readonly alive: Uint8Array;
  /** Stack of free slot indices. */
  private readonly freeSlots: Int32Array;

  private freeCount = 0;
  /** Highest slot index ever handed out; bounds iteration. */
  private highWater = 0;
  private liveCount = 0;

  constructor(capacity: number = ENGINE.MAX_ENTITIES) {
    if (capacity > INDEX_MASK + 1) {
      throw new Error(`Registry capacity ${capacity} exceeds the ${INDEX_BITS}-bit index space`);
    }
    this.capacity = capacity;
    this.generations = new Uint16Array(capacity);
    this.alive = new Uint8Array(capacity);
    this.freeSlots = new Int32Array(capacity);
  }

  /** Live entity count. */
  get count(): number {
    return this.liveCount;
  }

  /** Allocate an entity. Throws when the registry is full — silent failure would be worse. */
  create(): EntityId {
    let index: number;
    if (this.freeCount > 0) {
      this.freeCount -= 1;
      index = this.freeSlots[this.freeCount];
    } else {
      if (this.highWater >= this.capacity) {
        throw new Error(`Registry is full (capacity ${this.capacity})`);
      }
      index = this.highWater;
      this.highWater += 1;
    }
    this.alive[index] = 1;
    this.liveCount += 1;
    return (((this.generations[index] & GENERATION_MASK) << INDEX_BITS) | index) >>> 0;
  }

  /** True if the handle refers to a currently live entity. */
  isAlive(id: EntityId): boolean {
    if (id < 0) return false;
    const index = entityIndex(id);
    if (index >= this.highWater) return false;
    return this.alive[index] === 1 && this.generations[index] === entityGeneration(id);
  }

  /** Destroy an entity. Destroying a stale or dead handle is a no-op and returns false. */
  destroy(id: EntityId): boolean {
    if (!this.isAlive(id)) return false;
    const index = entityIndex(id);
    this.alive[index] = 0;
    this.generations[index] = (this.generations[index] + 1) & GENERATION_MASK;
    this.freeSlots[this.freeCount] = index;
    this.freeCount += 1;
    this.liveCount -= 1;
    return true;
  }

  /**
   * Visit every live entity. Allocation-free; the callback must not create or destroy entities
   * during iteration — queue those and apply after.
   */
  forEach(visit: (id: EntityId) => void): void {
    for (let index = 0; index < this.highWater; index += 1) {
      if (this.alive[index] === 1) {
        visit(((((this.generations[index] & GENERATION_MASK) << INDEX_BITS) | index) >>> 0) as EntityId);
      }
    }
  }

  /** Drop all entities and bump every generation so outstanding handles go stale. */
  clear(): void {
    for (let index = 0; index < this.highWater; index += 1) {
      if (this.alive[index] === 1) {
        this.generations[index] = (this.generations[index] + 1) & GENERATION_MASK;
      }
      this.alive[index] = 0;
    }
    this.freeCount = 0;
    this.highWater = 0;
    this.liveCount = 0;
  }
}

/**
 * A pre-allocated component array indexed by entity slot.
 *
 * Values are constructed once at build time and reused, so attaching a component in the tick
 * costs no allocation. `add` returns the slot's instance for the caller to initialise in place.
 */
export class ComponentArray<T> {
  readonly name: string;

  private readonly values: T[];
  private readonly present: Uint8Array;
  private count_ = 0;

  constructor(name: string, factory: () => T, capacity: number = ENGINE.MAX_ENTITIES) {
    this.name = name;
    this.present = new Uint8Array(capacity);
    this.values = new Array<T>(capacity);
    for (let i = 0; i < capacity; i += 1) this.values[i] = factory();
  }

  /** Number of entities currently carrying this component. */
  get count(): number {
    return this.count_;
  }

  /** Attach to an entity and return the (recycled) instance to initialise in place. */
  add(id: EntityId): T {
    const index = entityIndex(id);
    if (this.present[index] === 0) {
      this.present[index] = 1;
      this.count_ += 1;
    }
    return this.values[index];
  }

  /** True if the entity carries this component. */
  has(id: EntityId): boolean {
    return this.present[entityIndex(id)] === 1;
  }

  /** The component, or undefined when absent. */
  get(id: EntityId): T | undefined {
    const index = entityIndex(id);
    return this.present[index] === 1 ? this.values[index] : undefined;
  }

  /** Detach from an entity. The instance is retained for reuse. */
  remove(id: EntityId): void {
    const index = entityIndex(id);
    if (this.present[index] === 1) {
      this.present[index] = 0;
      this.count_ -= 1;
    }
  }

  /** Detach from every entity. */
  clear(): void {
    this.present.fill(0);
    this.count_ = 0;
  }
}
